import { AlertTriangle } from 'lucide-react';
import type { ReactNode } from 'react';
import type { RowIssue } from '@obraplan/core/domain/schedule/purchase-template/index';
import { cn } from '@/lib/cn';
import type { StatusFilter } from './conferencia-model';
import type { LineCounts } from './purchase-model';

/**
 * As peças que as duas conferências — a das linhas de compra e a da mão de obra — repetem:
 * a caixa de "criar o que falta", os filtros por situação, a lista de problemas de leitura
 * e a de avisos. Existem à parte para que as duas telas não divirjam no que é o mesmo.
 */

/** Os filtros da tabela de linhas, com a contagem de cada situação. */
export function StatusFilters({
  filter,
  onFilter,
  counts,
  total,
}: {
  readonly filter: StatusFilter;
  readonly onFilter: (next: StatusFilter) => void;
  readonly counts: LineCounts;
  readonly total: number;
}) {
  const options = [
    ['ALL', 'Todas', total],
    ['CREATE', 'Criar', counts.create],
    ['UPDATE', 'Atualizar', counts.update],
    ['UNCHANGED', 'Iguais', counts.unchanged],
    ['ERROR', 'Com erro', counts.error],
  ] as const;
  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filtrar linhas">
      {options.map(([value, label, count]) => (
        <button
          key={value}
          type="button"
          aria-pressed={filter === value}
          onClick={() => onFilter(value)}
          className={cn(
            'min-h-9 rounded-full border px-3 py-1 text-xs md:min-h-0',
            filter === value
              ? 'border-(--color-brand) font-medium text-(--color-brand)'
              : 'border-(--color-border-subtle) text-(--color-content-muted)',
          )}
        >
          {label} ({count.toLocaleString('pt-BR')})
        </button>
      ))}
    </div>
  );
}

/** Problemas que a leitura achou no arquivo — o arquivo continua valendo. */
export function ReaderIssues({
  id,
  title,
  issues,
}: {
  readonly id: string;
  readonly title: string;
  readonly issues: readonly RowIssue[];
}) {
  if (issues.length === 0) return null;
  return (
    <section aria-labelledby={id} className="flex flex-col gap-2">
      <h3 id={id} className="flex items-center gap-2 text-sm font-medium">
        <AlertTriangle className="size-4 text-(--color-state-warn)" aria-hidden="true" />
        {title} ({issues.length.toLocaleString('pt-BR')})
      </h3>
      <ul className="max-h-32 overflow-y-auto text-xs text-(--color-content-muted)">
        {issues.slice(0, 20).map((issue, index) => (
          <li key={`${String(issue.rowNumber)}-${String(index)}`}>
            Linha {issue.rowNumber}: {issue.message}
          </li>
        ))}
        {issues.length > 20 && <li>… e mais {issues.length - 20}.</li>}
      </ul>
    </section>
  );
}

/** Avisos do servidor — a linha entra assim mesmo, e quem decide é gente. */
export function Warnings({
  id,
  title,
  warnings,
}: {
  readonly id: string;
  readonly title: string;
  readonly warnings: readonly string[];
}) {
  if (warnings.length === 0) return null;
  return (
    <section aria-labelledby={id} className="flex flex-col gap-2">
      <h3 id={id} className="flex items-center gap-2 text-sm font-medium">
        <AlertTriangle className="size-4 text-(--color-state-warn)" aria-hidden="true" />
        {title} ({warnings.length.toLocaleString('pt-BR')})
      </h3>
      <ul className="max-h-32 overflow-y-auto text-xs text-(--color-content-muted)">
        {warnings.slice(0, 20).map((warning, index) => (
          <li key={`${String(index)}-${warning}`}>{warning}</li>
        ))}
        {warnings.length > 20 && <li>… e mais {warnings.length - 20}.</li>}
      </ul>
    </section>
  );
}

/** O que a tela diz enquanto a prévia não chega, ou quando não há linha para conferir. */
export function PreviewStatus({
  previewError,
  isPreviewing,
}: {
  readonly previewError: string | undefined;
  readonly isPreviewing: boolean;
}) {
  if (previewError !== undefined) {
    return (
      <p role="alert" className="text-sm text-(--color-state-danger)">
        {previewError}
      </p>
    );
  }
  return (
    <p role="status" className="text-sm text-(--color-content-muted)">
      {isPreviewing ? 'Conferindo com o cadastro da obra…' : 'Sem linhas para conferir.'}
    </p>
  );
}

/** Uma caixa de "criar o que falta": desligada por padrão (RN-035), com o que ela cria. */
export function Option({
  id,
  checked,
  disabled = false,
  onChange,
  label,
  children,
}: {
  readonly id: string;
  readonly checked: boolean;
  readonly disabled?: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly label: string;
  readonly children?: ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <input
        id={id}
        type="checkbox"
        className="mt-1"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <div className="flex flex-col">
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        {children !== undefined && (
          <p className="text-xs text-(--color-content-muted)">{children}</p>
        )}
      </div>
    </div>
  );
}
